import { Controller } from "@hotwired/stimulus"
import { Announcer } from "~/utils/announcer"

// Connects to data-controller="timer"
// Self-contained timer controller that replaces Example1.Timer
// Handles quiz/section timers with warning styling and auto-submit
export default class extends Controller {
  static values = {
    submitTime: { type: Number, default: 35 }, // shutdown & score time in minutes
    warningTime: { type: Number, default: 30 }, // warning (background turns yellow) time in minutes
    stopwatchId: { type: String, default: "stopwatch" }, // ID of the stopwatch element
    incrementTime: { type: Number, default: 200 } // Timer speed in milliseconds
  }

  connect() {
    this.stopwatch = null
    this.timer = null
    this.currentTime = 0 // Current time in hundredths of a second
    this.startTime = 0
    this.announcedMilestones = new Set() // Track announced time warnings for screen readers

    // Get stopwatch element
    this.stopwatch = document.getElementById(this.stopwatchIdValue)
    if (!this.stopwatch) {
      console.warn(`[timer] Stopwatch element #${this.stopwatchIdValue} not found`)
      return
    }

    // Get submit time and warning time from submit button if available
    const submitButton = document.getElementById('submit')
    if (submitButton) {
      const submitTimeAttr = submitButton.getAttribute('data-submit-time')
      const warningTimeAttr = submitButton.getAttribute('data-warning-time')
      if (submitTimeAttr) {
        this.submitTimeValue = parseInt(submitTimeAttr, 10)
      }
      if (warningTimeAttr) {
        this.warningTimeValue = parseInt(warningTimeAttr, 10)
      }
    }

    // Auto-start timer if start-btn exists and is hidden (for quizzes/take.html.erb)
    // On practice pages, the start-btn is visible and user clicks it, so don't auto-start
    const startBtn = document.querySelector('.start-btn')
    if (startBtn && (startBtn.classList.contains('hide') || startBtn.style.display === 'none' || window.getComputedStyle(startBtn).display === 'none')) {
      // Wait for jQuery.timer to be available
      this.waitForJQueryTimer(() => {
        this.initializeTimer()
        // Auto-start the timer
        if (this.timer) {
          this.timer.toggle()
        }
      })
    }
  }

  disconnect() {
    if (this.timer) {
      try {
        this.timer.stop()
      } catch (e) {
        console.warn('[timer] Error stopping timer:', e)
      }
    }
    this.timer = null
  }

  toggle(event) {
    if (event) {
      event.preventDefault()
    }

    if (!this.timer) {
      this.waitForJQueryTimer(() => {
        this.initializeTimer()
        if (this.timer) {
          this.timer.toggle()
        }
      })
    } else {
      this.timer.toggle()
    }
  }

  stop(event) {
    if (event) {
      event.preventDefault()
    }

    if (this.timer) {
      this.timer.stop()
    }
  }

  resetStopwatch() {
    this.currentTime = 0
    if (this.timer) {
      this.timer.stop().once()
    }
  }

  waitForJQueryTimer(callback, retryCount = 0) {
    const maxRetries = 50 // 5 seconds max wait

    if (window.jQuery && window.jQuery.timer) {
      callback()
    } else if (retryCount < maxRetries) {
      setTimeout(() => this.waitForJQueryTimer(callback, retryCount + 1), 100)
    } else {
      console.error('[timer] jQuery.timer failed to load after maximum retries')
    }
  }

  initializeTimer() {
    if (!this.stopwatch) {
      this.stopwatch = document.getElementById(this.stopwatchIdValue)
    }

    if (!this.stopwatch) {
      console.error(`[timer] Stopwatch element #${this.stopwatchIdValue} not found`)
      return
    }

    if (this.timer) {
      // Timer already initialized
      return
    }

    // Create timer using jQuery.timer plugin
    this.timer = window.jQuery.timer(
      () => this.updateTimer(),
      this.incrementTimeValue,
      false // Don't start immediately
    )
  }

  updateTimer() {
    if (!this.stopwatch) {
      return
    }

    // Format and display time
    const formattedTime = this.formatTime(this.currentTime)
    this.stopwatch.textContent = formattedTime
    this.stopwatch.setAttribute("data-clock", this.currentTime / 100)

    const timeInSeconds = this.currentTime / 100

    // Screen reader announcements for time milestones
    this.announceTimeWarnings(timeInSeconds)

    // Warning styling when warning time is reached
    if (timeInSeconds > this.warningTimeValue * 60 && timeInSeconds < (this.warningTimeValue * 60 + 1)) {
      // First second of warning - apply initial warning styles
      const compSecBtm = document.querySelector(".comp-sec-btm")
      const questionContainers = document.querySelectorAll(".lsat-question-container")
      const normalTxt = document.querySelectorAll(".normal-txt")

      if (compSecBtm) {
        compSecBtm.style.backgroundColor = "#FFFFEA"
      }
      questionContainers.forEach(el => {
        el.style.borderColor = "#FFFFEA"
      })
      normalTxt.forEach(el => {
        el.style.borderColor = "#FFFFEA"
      })
    }

    // Continue warning styling after warning time
    if (timeInSeconds > this.warningTimeValue * 60) {
      const questionContainers = document.querySelectorAll(".lsat-question-container")
      const selectedQuestion = document.querySelector(".lsat-question-selected")

      questionContainers.forEach(el => {
        el.style.borderColor = "#FFFFEA"
      })
      if (selectedQuestion) {
        selectedQuestion.style.borderColor = "#4592c9"
      }
    }

    // Auto-submit when submit time is reached
    if (timeInSeconds > this.submitTimeValue * 60) {
      this.timer.stop()

      const compSecBtm = document.querySelector(".comp-sec-btm")
      const startBtn = document.querySelector(".start-btn")
      const submitButton = document.getElementById("submit")

      if (compSecBtm) {
        compSecBtm.classList.add("fade-block")
      }
      if (startBtn) {
        startBtn.style.visibility = "hidden"
      }
      if (submitButton) {
        submitButton.classList.add("finish-btn")
        submitButton.textContent = "Submitting Answers..."
        // Delay 1 second and then click submit
        setTimeout(() => {
          submitButton.click()
        }, 1000)
      }
    }

    // Update current time
    if (this.startTime === 0) {
      this.startTime = new Date().getTime() - this.incrementTimeValue
    }
    this.currentTime = ((new Date().getTime() - this.startTime) / 10)
  }

  // Helper functions (same as in custom-function.js)
  pad(number, length) {
    var str = '' + number
    while (str.length < length) { str = '0' + str }
    return str
  }

  formatTime(time) {
    var min = parseInt(time / 6000),
      sec = parseInt(time / 100) - (min * 60),
      hundredths = this.pad(time - (sec * 100) - (min * 6000), 2)
    return (min > 0 ? this.pad(min, 2) : "00") + ":" + this.pad(sec, 2)
  }

  // Announce time warnings for screen reader users
  announceTimeWarnings(timeInSeconds) {
    const submitTimeSeconds = this.submitTimeValue * 60
    const warningTimeSeconds = this.warningTimeValue * 60
    const remainingSeconds = submitTimeSeconds - timeInSeconds

    // Helper to announce only once per milestone
    const announceOnce = (key, message, assertive = false) => {
      if (!this.announcedMilestones.has(key)) {
        this.announcedMilestones.add(key)
        if (assertive) {
          Announcer.announceAssertive(message)
        } else {
          Announcer.announce(message)
        }
      }
    }

    // Announce when warning time is reached (5 minutes remaining)
    if (timeInSeconds >= warningTimeSeconds && remainingSeconds <= 300) {
      announceOnce('5min', '5 minutes remaining')
    }

    // Announce 1 minute remaining
    if (remainingSeconds <= 60 && remainingSeconds > 30) {
      announceOnce('1min', '1 minute remaining', true)
    }

    // Announce 30 seconds remaining
    if (remainingSeconds <= 30 && remainingSeconds > 0) {
      announceOnce('30sec', '30 seconds remaining', true)
    }

    // Announce time is up
    if (remainingSeconds <= 0) {
      announceOnce('timeup', 'Time is up. Submitting answers.', true)
    }
  }
}
